import { format } from 'date-fns';

import {
  type ElementSizingMode,
  type IconSelectOption,
  type IPropEditorResolver,
  type PropertiesConfig,
  type PropertiesConfigInputOptionsDatetime,
  type PropertiesConfigItemDefinition,
  type PropertiesConfigSubsection,
  TIME_FORMAT_OPTIONS,
} from '@blaze-shared/elements-domain';
import {
  DATE_FORMAT_TO_FNS_MAP,
  DATE_FORMATS,
  MASKABLE_DATE_FORMATS,
} from '@blaze-shared/shared-util';
import {
  type DateFormat,
  type DatetimeDefaultDateValue,
  type DatetimeDefaultTimeValue,
  getFirstDayOfWeek,
} from '@blaze-shared/shared-util';
import { getValueByPath } from '@blaze-shared/workflow-domain';

import { getFormlikeBinding } from '../../canvas-elements/element-model/data-binding';
import {
  propsConfigHelper as epc,
  type SubsectionOptions,
} from '../../utils/elements-props-config.helpers';

import { type DatetimeInputPropsModel } from './datetime-input.props-model';

export const DEFAULT_WIDTH = 356;
export const MIN_SIZE = 48;

const MAX_WIDTH = 900;
const MAX_HEIGHT = 150;
const WIDTH_OPTIONS: ElementSizingMode[] = ['fixed', 'full'];
const HEIGHT_OPTIONS: ElementSizingMode[] = ['min'];

export function getPropsConfig(): PropertiesConfig {
  return epc.initConfig('datetimeInput', [
    epc.section('content', 'Content', [
      dateSection('content.date', {
        dataBindings: [
          {
            key: 'value',
            label: 'Value',
            sources: 'field',
            bindings: [{ property: 'value', label: 'Value', dataType: 'DateTime' }],
          },
        ],
        optional: isDatetimeSectionOptional,
        hideWhen: (_, __, model) =>
          !getValueByPath(model, 'content.date.enabled') && isBoundToForm(model),
      }),
      timeSection('content.time', 'Time', true, {
        dataBindings: [
          {
            key: 'value',
            label: 'Value',
            sources: 'field',
            bindings: [{ property: 'value', label: 'Value', dataType: 'DateTime' }],
          },
        ],
        optional: isDatetimeSectionOptional,
        hideWhen: (_, __, model) =>
          !getValueByPath(model, 'content.time.enabled') && isBoundToForm(model),
      }),
      epc.subsections.elementSizeSubsection(
        MIN_SIZE,
        MAX_WIDTH,
        MAX_HEIGHT,
        WIDTH_OPTIONS,
        HEIGHT_OPTIONS,
      ),
      epc.subsections.visibilitySubsection(),
      epc.subsections.labelContentSubsection('content'),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.general', 'Margin', [
          epc.fields.spacingAndAlignField('style.general.offset', {
            hasMargin: true,
          }),
        ]),

        epc.subsections.subsection('style.general', 'General', [
          epc.fields.colorField('style.general.accent', 'Accent', {
            allowGradient: false,
            allowNone: false,
          }),
        ]),

        epc.subsections.subsection('style.inputFont', 'Text Input Font', [
          ...epc.fields.fontFamilyAndWeightFields(
            'style.inputFont.fontFamily',
            'style.inputFont.fontWeight',
          ),
          epc.fields.fontStyleField('style.inputFont.style', 'Style', ['bold']),
          epc.fields.sliderField('style.inputFont.fontSize', 'Size', {
            units: [{ min: 8, max: 72, unit: 'px' }],
          }),
        ]),

        epc.subsections.subsection('style.input', 'Text Input Style', [
          epc.fields.colorField('style.input.value', 'Value', {
            allowGradient: false,
            allowNone: false,
          }),
          epc.fields.colorField('style.input.fill', 'Fill', { allowNone: false }),
          epc.fields.colorField('style.input.border', 'Border', { allowGradient: false }),
          epc.fields.radiusField('style.input.radius'),
        ]),

        epc.subsections.labelStyleSubsection(
          {
            color: 'style.general.title',
            enabled: 'content.title.enabled',
            fontFamily: 'style.labelBlock.fontFamily',
            fontSize: 'style.labelBlock.fontSize',
            fontStyle: 'style.labelBlock.fontStyle',
            fontWeight: 'style.labelBlock.fontWeight',
            horizontalAlign: 'content.title.align',
            icon: 'content.title.icon',
            iconColor: 'content.title.iconColor',
            iconPosition: 'content.title.iconPosition',
            iconSize: 'style.labelBlock.iconSize',
            lineHeight: 'style.labelBlock.lineHeight',
            position: 'content.title.position',
            width: 'content.title.width',
          },
          {
            excludedPositions: ['right'],
          },
        ),
      ],
      true,
    ),
  ]);
}

function isBoundToForm(model: DatetimeInputPropsModel): boolean {
  return !!getFormlikeBinding(model);
}

function isDatetimeSectionOptional(
  model: any,
  elementId: string,
  propEditorResolver: IPropEditorResolver,
): boolean {
  if (isBoundToForm(model)) {
    return false;
  }
  return !propEditorResolver.resolveValue('IS_FILTER', { elementId });
}

const firstDayOfWeek = getFirstDayOfWeek();
const [dowFirst, dowLast] = firstDayOfWeek ? ['Sunday', 'Saturday'] : ['Monday', 'Sunday'];
function dateSection(path: string, options?: SubsectionOptions): PropertiesConfigSubsection {
  return epc.subsections.subsection(
    path,
    'Date',
    [
      epc.utils.conditionalField(
        epc.fields.selectFieldFromList(
          `${path}.format`,
          'Format',
          DATE_FORMATS.map((value) => ({
            value,
            label: format(Date.now(), DATE_FORMAT_TO_FNS_MAP[value]),
          })),
          { allowNone: false, showSelection: true },
        ),
        `${path}.disableDatepicker`,
        (disableDatepicker) => disableDatepicker,
      ),
      epc.utils.conditionalField(
        epc.fields.selectFieldFromList(
          `${path}.format`,
          'Format',
          MASKABLE_DATE_FORMATS.map((value) => ({
            value,
            label: format(Date.now(), DATE_FORMAT_TO_FNS_MAP[value]),
          })),
          { allowNone: false, showSelection: true },
        ),
        `${path}.disableDatepicker`,
        (disableDatepicker) => !disableDatepicker,
      ),
      dateField(`${path}.min`, 'Min date', {
        max: (model) => getValueByPath(model, `${path}.max`) ?? undefined,
        allowedValueTypes: ['fixed', 'relative'],
      }),
      dateField(`${path}.max`, 'Max date', {
        min: (model) => getValueByPath(model, `${path}.min`) ?? undefined,
        allowedValueTypes: ['fixed', 'relative'],
      }),
      epc.fields.radiosField(`${path}.defaultValue`, 'Default', <
        IconSelectOption<DatetimeDefaultDateValue>[]
      >[
        { value: 'none', label: 'None' },
        { value: 'today', label: 'Today' },
        { value: 'other', label: 'Other' },
      ]),
      ...epc.utils.conditionalFields(
        [
          epc.fields.selectFieldFromList(
            `${path}.defaultValueFunction`,
            'Date',
            [
              { value: 'startOfWeek', label: `${dowFirst} of the current week` },
              { value: 'endOfWeek', label: `${dowLast} of the current week` },
              { value: 'startOfMonth', label: '1st day of the current month' },
              { value: 'endOfMonth', label: 'last day of the current month' },
              { value: 'startOfNextMonth', label: '1st day of the following month' },
              { value: 'startOfYear', label: '1st day of the current year' },
            ],
            { showSelection: true, allowNone: false, maxWidth: '177px' },
          ),
          epc.fields.sliderField(`${path}.defaultValueOffset`, 'Offset', [
            { min: -31, max: 31, unit: 'days' },
            { min: -12, max: 12, unit: 'weeks' },
            { min: -24, max: 24, unit: 'months' },
            { min: -5, max: 5, unit: 'years' },
          ]),
        ],
        `${path}.defaultValue`,
        (value) => value !== 'other',
      ),
      epc.utils.observedField(
        epc.fields.toggleField(`${path}.disableDatepicker`, 'Show calendar', { isNegated: true }),
        (model, value: DateFormat) => {
          if (MASKABLE_DATE_FORMATS.includes(value)) {
            return {};
          }
          return { [`${path}.format`]: 'MM/DD/YYYY' } satisfies { [key in string]: DateFormat };
        },
      ),
      epc.fields.toggleField('content.date.readonly', 'Read Only'),
    ],
    { optional: true, ...options },
  );
}

function timeSection(
  path: string,
  label: string,
  isFormatVisible = true,
  options?: SubsectionOptions,
): PropertiesConfigSubsection {
  return epc.subsections.subsection(
    path,
    label,
    [
      ...(isFormatVisible ? [timeFormatField(`${path}.format`, 'Format')] : []),
      timeField(`${path}.min`, 'Min hour', {
        max: (model) => getValueByPath(model, `${path}.max`) ?? undefined,
      }),
      timeField(`${path}.max`, 'Max hour', {
        min: (model) => getValueByPath(model, `${path}.min`) ?? undefined,
      }),
      epc.fields.radiosField(`${path}.defaultValue`, 'Default', <
        IconSelectOption<DatetimeDefaultTimeValue>[]
      >[
        { value: 'none', label: 'None' },
        { value: 'now', label: 'Now' },
        { value: 'other', label: 'Other' },
      ]),
      ...epc.utils.conditionalFields(
        [
          epc.fields.selectFieldFromList(
            `${path}.defaultValueFunction`,
            'Time',
            [
              { value: 'startOfDay', label: 'Start of today' },
              { value: 'noon', label: 'Noon' },
              { value: 'endOfDay', label: 'End of today' },
              { value: 'custom', label: 'Custom' },
            ],
            { showSelection: true, allowNone: false },
          ),
          epc.utils.conditionalField(
            timeField(`${path}.defaultTime`, 'Default', {
              defaultTime: (model) => getValueByPath(model, `${path}.defaultTime`) ?? undefined,
            }),
            `${path}.defaultValueFunction`,
            (value) => value !== 'custom',
          ),
          epc.fields.sliderField(`${path}.defaultValueOffset`, 'Offset', [
            { min: -60, max: 60, unit: 'min' },
            { min: -24, max: 24, unit: 'hrs' },
          ]),
        ],
        `${path}.defaultValue`,
        (value) => value !== 'other',
      ),
      epc.fields.toggleField('content.time.readonly', 'Read Only'),
    ],
    { optional: true, ...options },
  );
}

function dateField(
  path: string,
  label: string,
  inputOptions?: Partial<PropertiesConfigInputOptionsDatetime>,
): PropertiesConfigItemDefinition<'datetime'> {
  return {
    path,
    label,
    inputType: 'datetime',
    inputOptions: {
      clearable: true,
      mode: 'date',
      allowedValueTypes: ['fixed'],
      ...inputOptions,
    },
  };
}

function timeField(
  path: string,
  label: string,
  inputOptions?: Partial<PropertiesConfigInputOptionsDatetime>,
): PropertiesConfigItemDefinition<'datetime'> {
  return {
    path,
    label,
    inputType: 'datetime',
    inputOptions: {
      clearable: true,
      mode: 'time',
      allowedValueTypes: ['fixed'],
      ...inputOptions,
    },
  };
}

function timeFormatField(path: string, label: string): PropertiesConfigItemDefinition<'radios'> {
  return {
    path,
    inputType: 'radios',
    label,
    inputOptions: {
      renderType: 'text',
      options: TIME_FORMAT_OPTIONS,
      defaultValue: '12h',
      stretched: true,
    },
  };
}
